import type { StaffFinanceViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";

interface Props {
  vm: StaffFinanceViewModel;
}

export default function StaffFinanceView({ vm }: Props) {
  const state = useViewModel(vm);

  if (!vm.canReadBusiness() && !vm.canManageInvestors()) {
    return <p className="text-sm text-gray-500">Your role cannot view finance.</p>;
  }

  const summary = state.summary;

  return (
    <section className="space-y-6">
      <h2 className="text-lg font-bold">Finance</h2>
      {state.error && <p className="text-sm text-red-600">{state.error}</p>}
      {state.loading && <p className="text-sm text-gray-500">Loading…</p>}
      {vm.canReadBusiness() && summary && (
        <dl className="grid grid-cols-2 gap-4 rounded-lg border border-gray-100 bg-white p-4 text-sm md:grid-cols-4">
          <div>
            <dt className="text-gray-500">Sales</dt>
            <dd className="text-xl font-bold">{summary.salesCount}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Revenue</dt>
            <dd className="text-xl font-bold">${(summary.soldCents / 100).toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Expenses</dt>
            <dd className="text-xl font-bold">${(summary.expenseCents / 100).toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Gross profit</dt>
            <dd className="text-xl font-bold">${(summary.grossProfitCents / 100).toLocaleString()}</dd>
          </div>
        </dl>
      )}
      {vm.canManageInvestors() && (
        <div>
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Investors</h3>
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
            {state.investors.map((inv) => (
              <li key={inv.id} className="p-3 text-sm">
                <span className="font-semibold">{inv.name}</span>
                {inv.email ? <span className="text-gray-500"> · {inv.email}</span> : null}
              </li>
            ))}
            {state.investors.length === 0 && !state.loading && (
              <li className="p-3 text-sm text-gray-500">No investors on file.</li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
